/**
 * Context Binding Engine — fact matchers.
 *
 * Disambiguate one-tag-many-fields on the SOURCE SPAN. When a single
 * extraction tag (`parcel.acres`, `schedule.inservice`) fans out to many
 * target fields, the matcher decides WHICH binding row fires for a given
 * fact by reading `sourceText`:
 *
 *   - `'always'`      — tag → single field; fires unconditionally.
 *   - `'subField'`    — fires when `fact.field === field`.
 *   - `'spanRegex'`   — fires when a regex matches the span (e.g.
 *                       /buildable|developable/ → developableAcres).
 *   - `'locusKeyword'`— fires when one of the keywords occurs in the span
 *                       AND the fact's numeric value is the one bound
 *                       NEAREST that keyword. This is the 332-vs-800 acres
 *                       and NTP-vs-COD which-of-many case.
 *
 * The locus check REUSES the proven nearest-keyword binder
 * `valueSupportedNearKeyword` from `fieldSchema.ts` (the same algorithm
 * that catches IN-RANGE wrong values in the doc / intent channels) so the
 * CBE inherits its conservative truth table rather than re-implementing it.
 *
 * Pure / synchronous. No I/O.
 */
import {
  numericAppearsInSpan,
  valueSupportedNearKeyword,
  LOCUS_KEYWORD_WINDOW_CHARS,
} from '../../lib/sed/fieldSchema';
import type { CbeFact, CbeFactMatcher } from './cbeTypes';

/** Extract the fact's numeric value, if it has one. */
function factNumeric(fact: CbeFact): number | null {
  if (typeof fact.value === 'number' && Number.isFinite(fact.value)) return fact.value;
  if (typeof fact.value === 'string') {
    const m = fact.value.match(/-?\d+(?:\.\d+)?/);
    if (m) {
      const n = Number(m[0]);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/**
 * Does `matcher` fire for `fact`? The caller has already matched the tag;
 * this decides the which-of-many. Pure.
 *
 *   - `always`      → true.
 *   - `subField`    → fact.field === matcher.field.
 *   - `spanRegex`   → matcher.re.test(fact.sourceText).
 *   - `locusKeyword`→ a keyword occurs in the span AND (when the fact has a
 *                     numeric value) that value is SUPPORTED nearest the
 *                     keyword (`valueSupportedNearKeyword`). With no
 *                     keyword in the span the row does NOT fire (a
 *                     locus-keyed row needs its keyword present to claim
 *                     the fact) — this is stricter than the doc-channel's
 *                     skip-when-absent default because here multiple rows
 *                     COMPETE for one fact, so an absent keyword must yield
 *                     the fact to a sibling row.
 */
export function matchesFact(matcher: CbeFactMatcher, fact: CbeFact): boolean {
  switch (matcher.kind) {
    case 'always':
      return true;

    case 'subField':
      return fact.field === matcher.field;

    case 'spanRegex':
      // Reset lastIndex for safety if the regex is /g (it shouldn't be).
      matcher.re.lastIndex = 0;
      return matcher.re.test(fact.sourceText);

    case 'locusKeyword': {
      const span = fact.sourceText ?? '';
      if (span === '') return false;
      const lower = span.toLowerCase();
      const keywordPresent = matcher.keywords.some(kw => kw !== '' && lower.includes(kw.toLowerCase()));
      if (!keywordPresent) return false;

      const num = factNumeric(fact);
      // Non-numeric fact: the keyword's presence is enough to claim it.
      if (num === null) return true;

      // Numeric fact: the value must bind nearest THIS row's keyword.
      const window = matcher.window ?? LOCUS_KEYWORD_WINDOW_CHARS;
      return valueSupportedNearKeyword(span, num, matcher.keywords, window);
    }

    default: {
      // Exhaustiveness guard — unreachable.
      const _exhaustive: never = matcher;
      return Boolean(_exhaustive);
    }
  }
}

/**
 * Of the candidate rows that fan out from one tag, return the SINGLE best
 * row to fire for `fact`, or `null` when none matches. When several rows
 * match (e.g. two locus rows both keyword-present), prefer the most
 * specific:
 *   subField / spanRegex / locusKeyword  >  always
 * and among locus rows, the one whose keyword sits NEAREST the fact's
 * numeric value (tightest binding). This avoids a generic `always` row
 * stealing a fact a specific locus row should own.
 */
export function pickBestMatcher<T extends { factMatcher: CbeFactMatcher }>(
  candidates: readonly T[],
  fact: CbeFact,
): T | null {
  const matched = candidates.filter(c => matchesFact(c.factMatcher, fact));
  if (matched.length === 0) return null;
  if (matched.length === 1) return matched[0]!;

  // Rank: non-`always` matchers outrank `always`.
  const specific = matched.filter(c => c.factMatcher.kind !== 'always');
  const pool = specific.length > 0 ? specific : matched;
  if (pool.length === 1) return pool[0]!;

  // Among the pool, prefer locus rows by tightest keyword→value distance.
  const num = factNumeric(fact);
  if (num !== null) {
    let best: { row: T; dist: number } | null = null;
    for (const c of pool) {
      if (c.factMatcher.kind !== 'locusKeyword') continue;
      const d = nearestKeywordDistance(fact.sourceText, num, c.factMatcher.keywords);
      if (d !== null && (best === null || d < best.dist)) best = { row: c, dist: d };
    }
    if (best) return best.row;
  }

  // Stable fallback: first in declared order.
  return pool[0]!;
}

/**
 * Distance (chars) from the fact's numeric value to its nearest keyword in
 * the span, or `null` when neither the keyword nor the value is present.
 * Smaller = tighter binding. Mirrors `valueSupportedNearKeyword`'s
 * positional scan.
 */
function nearestKeywordDistance(
  span: string,
  value: number,
  keywords: readonly string[],
): number | null {
  if (!span || !Number.isFinite(value)) return null;
  if (!numericAppearsInSpan(value, span)) return null;
  const lower = span.toLowerCase();
  const tol = Math.max(1e-9, Math.abs(value) * 1e-6);

  // Positions of the value token(s).
  const valuePositions: number[] = [];
  const numRe = /(-?\d+(?:\.\d+)?)/g;
  for (let m = numRe.exec(span); m !== null; m = numRe.exec(span)) {
    const v = Number(m[1]);
    if (Number.isFinite(v) && Math.abs(v - value) <= tol) valuePositions.push(m.index);
  }
  if (valuePositions.length === 0) return null;

  let best: number | null = null;
  for (const kw of keywords) {
    const needle = kw.toLowerCase();
    if (needle === '') continue;
    for (let idx = lower.indexOf(needle); idx >= 0; idx = lower.indexOf(needle, idx + needle.length)) {
      for (const vp of valuePositions) {
        const d = Math.abs(idx - vp);
        if (best === null || d < best) best = d;
      }
    }
  }
  return best;
}
